// Normalizes any uploaded photo (profile/cover/portfolio) into a small, universally
// renderable JPEG before it reaches Supabase Storage. Two problems this solves:
//  1. HEIC (default iPhone camera format) can't be rendered inline via <img> in most
//     browsers, even Safari — needs a WASM decode step (heic2any).
//  2. Phone cameras produce multi-megapixel, multi-megabyte originals with no built-in
//     size cap — these were being uploaded and served as-is, causing slow/inconsistent
//     loads for small avatar/card thumbnails. Downscaling + re-encoding fixes both
//     HEIC and ordinary JPEG/PNG uploads in one pass.
export async function toUploadableImage(file, { maxDim = 1024, quality = 0.85 } = {}) {
  let working = file;

  const isHeic = file.type === "image/heic" || file.type === "image/heif" ||
    /\.hei[cf]$/i.test(file.name || "");
  if (isHeic) {
    try {
      const heic2any = (await import("heic2any")).default;
      const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      working = new File([blob], (file.name || "photo").replace(/\.hei[cf]$/i, "") + ".jpg", { type: "image/jpeg" });
    } catch (e) {
      console.error("[FADED] HEIC conversion failed:", e);
    }
  }

  try {
    // imageOrientation:"from-image" applies the photo's EXIF rotation flag before
    // drawing to canvas — without it, portrait phone photos can come out sideways
    // once the EXIF tag is dropped from the re-encoded output.
    const bitmap = await createImageBitmap(working, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
    if (blob) {
      const newName = (working.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      return new File([blob], newName, { type: "image/jpeg" });
    }
  } catch (e) {
    console.error("[FADED] image resize/compress failed:", e);
  }
  return working;
}
